import { jsPDF } from "jspdf";
import {
  fmtCurrency,
  centsToReais,
  formatOrderNumber,
  formatDatePDF,
  formatPaymentMethod,
  formatCNPJ,
  formatZipcode,
  getUnitPrice,
  getProductLineTotal,
  getOrderSubtotal,
  getDeliveryFee,
  getCustomerCPF,
  getCustomerAddress,
  formatAddressLine,
  fetchImageAsBase64,
} from "./pdfUtils";

const C = {
  black:  [17, 17, 17],
  dark:   [51, 51, 51],
  mid:    [100, 100, 100],
  muted:  [150, 150, 150],
  border: [210, 210, 210],
  bg:     [248, 248, 248],
  white:  [255, 255, 255],
};

const W   = 210;       // A4 width mm
const H   = 297;       // A4 height mm
const ML  = 13;        // margin left
const MR  = 13;        // margin right
const CW  = W - ML - MR;
const FH  = 20;        // footer height

// Left/right column split for header
const LCW = 95;        // left column width
const RCX = ML + LCW + 6; // right column x start

const COMPANY = {
  brand:  "SVELTA",
  street: "Rua Alexandre Siqueira, 175, Bairro Caiçaras",
  city:   "Belo Horizonte – MG, CEP: 30775-540",
  cnpj:   "34124538000120",
  email:  "contato@svelta.com.br",
  phone:  "(31) 99192-0008",
  site:   "www.svelta.com.br",
};

function setColor(doc, rgb, type = "text") {
  if (type === "text") doc.setTextColor(...rgb);
  else if (type === "fill") doc.setFillColor(...rgb);
  else doc.setDrawColor(...rgb);
}

function hline(doc, x1, y, x2, color = C.border, lw = 0.25) {
  doc.setLineWidth(lw);
  setColor(doc, color, "draw");
  doc.line(x1, y, x2, y);
}

function txt(doc, str, x, y, opts = {}) {
  const { size = 7.5, bold = false, color = C.dark, align = "left" } = opts;
  doc.setFontSize(size);
  doc.setFont("helvetica", bold ? "bold" : "normal");
  setColor(doc, color);
  doc.text(String(str ?? ""), x, y, { align });
}

export async function generateInvoicePDF(order, logoSrc) {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  let y = 12;

  const c    = order.customer || {};
  const addr = getCustomerAddress(order);
  const cpf  = getCustomerCPF(order);

  // ── logo ──────────────────────────────────────────────────────────────────
  if (logoSrc) {
    try {
      doc.addImage(logoSrc, "PNG", ML, y, 36, 9, undefined, "FAST");
    } catch {
      txt(doc, COMPANY.brand, ML, y + 6, { size: 14, bold: true, color: C.black });
    }
  } else {
    txt(doc, COMPANY.brand, ML, y + 6, { size: 14, bold: true, color: C.black });
  }
  y += 15;   // gap between logo and the two data blocks

  // ── two-column block: [company data] | [customer data] ────────────────────
  // Both blocks start at the same Y
  const blockY = y;

  // ── LEFT: company data ────────────────────────────────────────────────────
  txt(doc, "DADOS DA EMPRESA", ML, y, { size: 6.5, bold: true, color: C.mid });
  y += 1.5;
  hline(doc, ML, y, ML + LCW, [200, 200, 200], 0.2);
  y += 3.5;

  txt(doc, COMPANY.street, ML, y, { size: 7, color: C.dark }); y += 3.8;
  txt(doc, COMPANY.city,   ML, y, { size: 7, color: C.dark }); y += 3.8;
  txt(doc, `CNPJ: ${formatCNPJ(COMPANY.cnpj)}`, ML, y, { size: 7, color: C.dark });
  y += 4.5;

  hline(doc, ML, y, ML + LCW, [200, 200, 200], 0.2);
  y += 3.5;

  const metaRows = [
    ["Nº do Pedido:",        formatOrderNumber(order)],
    ["Forma de Pagamento:",  formatPaymentMethod(order.paymentMethod)],
    ["Moeda:",               "R$ (Real)"],
  ];
  metaRows.forEach(([label, value]) => {
    txt(doc, label, ML,      y, { size: 7, bold: true, color: C.mid });
    txt(doc, value, ML + 35, y, { size: 7 });
    y += 3.8;
  });

  // ── RIGHT: customer data (starts at same Y as company block) ──────────────
  let ry = blockY;
  txt(doc, "DADOS DO CLIENTE", RCX, ry, { size: 6.5, bold: true, color: C.mid });
  ry += 1.5;
  hline(doc, RCX, ry, W - MR, [200, 200, 200], 0.2);
  ry += 3.5;

  txt(doc, c.name || "Sem cliente", RCX, ry, { size: 8, bold: true, color: C.black }); ry += 4.2;
  txt(doc, `CPF: ${cpf}`,           RCX, ry, { size: 7, color: C.dark });              ry += 3.8;

  if (addr) {
    const addrLine = formatAddressLine(addr);
    if (addrLine) {
      txt(doc, addrLine, RCX, ry, { size: 7, color: C.dark }); ry += 3.8;
    }
    if (addr.neighborhood) {
      txt(doc, `Bairro: ${addr.neighborhood}`, RCX, ry, { size: 7, color: C.dark }); ry += 3.8;
    }
    const cityPart = [addr.city, addr.state].filter(Boolean).join(" – ");
    if (cityPart) {
      txt(doc, cityPart, RCX, ry, { size: 7, color: C.dark }); ry += 3.8;
    }
    if (addr.zipcode) {
      txt(doc, `CEP: ${formatZipcode(addr.zipcode)}`, RCX, ry, { size: 7, color: C.dark });
    }
  }

  // advance y past whichever column is taller
  y = Math.max(y, ry) + 4;

  hline(doc, ML, y, W - MR, C.border, 0.4);
  y += 5;

  // ── products table ────────────────────────────────────────────────────────
  // Columns (all right-aligned except desc)
  // | # | img | description | qty | unit | total |
  // widths: 7 | 17 | 75 | 12 | 30 | 41 = 182 = CW
  const C_ITEM  = ML;           // left edge of item# col
  const C_IMG   = ML + 7;       // left edge of img col
  const C_DESC  = ML + 24;      // left edge of desc col
  const C_QTY   = ML + 99;      // center of qty col
  const C_UNIT  = ML + 147;     // right edge of unit col
  const C_TOTAL = W - MR;       // right edge of total col (=197)

  const IMG_SIZE = 16;   // square image side (mm)
  const ROW_H    = IMG_SIZE + 2;  // 18mm — 1mm pad top + image + 1mm pad bottom
  const TH       = 6;    // table header height

  // table header
  setColor(doc, C.black, "fill");
  doc.rect(ML, y, CW, TH, "F");
  doc.setFontSize(6.5); doc.setFont("helvetica", "bold");
  setColor(doc, C.white);
  doc.text("#",           C_ITEM + 1,  y + 4);
  doc.text("DESCRIÇÃO",   C_DESC,      y + 4);
  doc.text("QTD.",        C_QTY,       y + 4, { align: "center" });
  doc.text("VALOR UNIT.", C_UNIT,      y + 4, { align: "right" });
  doc.text("TOTAL",       C_TOTAL,     y + 4, { align: "right" });
  y += TH;

  for (let i = 0; i < (order.products || []).length; i++) {
    const p         = order.products[i];
    const unitPrice = getUnitPrice(p);
    const lineTotal = getProductLineTotal(p);

    if (y + ROW_H > H - FH - 45) {
      doc.addPage();
      y = 12;
    }

    if (i % 2 === 1) {
      setColor(doc, C.bg, "fill");
      doc.rect(ML, y, CW, ROW_H, "F");
    }

    txt(doc, String(i + 1), C_ITEM + 1, y + ROW_H / 2, { size: 7 });

    // product image — square, 1mm padding on all sides
    const imgB64 = p.ProductImage ? await fetchImageAsBase64(p.ProductImage) : null;
    if (imgB64) {
      try {
        doc.addImage(imgB64, "JPEG", C_IMG, y + 1, IMG_SIZE, IMG_SIZE, undefined, "FAST");
      } catch { /* skip broken */ }
    }

    // description: name + color + size — vertically centered
    const textY = y + 5.5;
    txt(doc, p.ProductName || "—", C_DESC, textY, { size: 7.5, bold: true });
    const subLines = [
      p.ProductColor?.colorName ? `Cor: ${p.ProductColor.colorName}` : null,
      p.ProductSize             ? `Tam.: ${p.ProductSize}`           : null,
    ].filter(Boolean);
    subLines.forEach((sl, si) => {
      txt(doc, sl, C_DESC, textY + (si + 1) * 3.8, { size: 6.5, color: C.muted });
    });

    txt(doc, String(p.ProductQuantity), C_QTY,  y + ROW_H / 2, { size: 7.5, align: "center" });
    txt(doc, fmtCurrency(unitPrice),    C_UNIT,  y + ROW_H / 2, { size: 7.5, align: "right" });
    txt(doc, fmtCurrency(lineTotal),    C_TOTAL, y + ROW_H / 2, { size: 7.5, bold: true, align: "right" });

    hline(doc, ML, y + ROW_H, W - MR, C.border, 0.2);
    y += ROW_H;
  }

  y += 4;

  // ── observations ──────────────────────────────────────────────────────────
  const obs = order.observation || order.observations || "";
  if (obs) {
    const obsLines = doc.splitTextToSize(obs, CW - 5);
    obsLines.forEach((ln, idx) => {
      txt(doc, ln, ML, y + idx * 3.8, { size: 7, color: C.mid });
    });
    y += obsLines.length * 3.8 + 3;
  }

  // ── totals ────────────────────────────────────────────────────────────────
  if (y + 36 > H - FH - 4) { doc.addPage(); y = 12; }

  const subtotal = getOrderSubtotal(order);
  const frete    = getDeliveryFee(order);
  const total    = centsToReais(order.amount || 0);
  const totX     = W - MR - 60;

  [
    ["Subtotal", fmtCurrency(subtotal)],
    ["Frete",    fmtCurrency(frete)],
  ].forEach(([label, value], i) => {
    const ry2 = y + i * 6;
    txt(doc, label, totX,      ry2, { size: 7.5, color: C.mid });
    txt(doc, value, W - MR,    ry2, { size: 7.5, color: C.mid, align: "right" });
  });

  y += 14;
  hline(doc, totX, y, W - MR, C.dark, 0.5);
  y += 5;
  txt(doc, "TOTAL",           totX,   y, { size: 9, bold: true, color: C.black });
  txt(doc, fmtCurrency(total), W - MR, y, { size: 9, bold: true, color: C.black, align: "right" });
  y += 6;

  hline(doc, ML, y + 2, W - MR, C.border, 0.3);

  // ── footer (all pages) ────────────────────────────────────────────────────
  const pageCount = doc.getNumberOfPages();
  for (let pg = 1; pg <= pageCount; pg++) {
    doc.setPage(pg);
    const fy = H - FH;

    setColor(doc, C.black, "fill");
    doc.rect(0, fy, W, FH, "F");

    // brand
    txt(doc, "SVELTA", ML, fy + 5.5, { size: 7.5, bold: true, color: C.white });

    // contact info (center)
    txt(doc, COMPANY.email,           W / 2, fy + 4,   { size: 6, color: [180, 180, 180], align: "center" });
    txt(doc, COMPANY.phone,           W / 2, fy + 8,   { size: 6, color: [180, 180, 180], align: "center" });
    txt(doc, "Seg.–Sex., 9h–18h",     W / 2, fy + 12,  { size: 5.5, color: [150, 150, 150], align: "center" });

    // fiscal (right)
    txt(doc, `CNPJ: ${formatCNPJ(COMPANY.cnpj)}`, W - MR, fy + 5,  { size: 6, color: [180, 180, 180], align: "right" });
    txt(doc, COMPANY.site,                          W - MR, fy + 9,  { size: 6, color: [180, 180, 180], align: "right" });
    txt(doc, "Emissão eletrônica",                  W - MR, fy + 13, { size: 5.5, color: [150, 150, 150], align: "right" });
  }

  return doc;
}
